import React, { useCallback, useLayoutEffect, useRef, useState, useMemo, useEffect, } from "react";
import { Select as BlueprintSelect } from "@blueprintjs/select";
import { ResizeSensor, Menu } from "@blueprintjs/core";
import classNames from "classnames";
import { getHideListenerModifier } from "utils/popper";
import ScrollBar from "components/ScrollBar";
import DefaultSelectButton from "./components/DefaultSelectButton";
import DefaultItemRenderer from "./components/DefaultItemRenderer";
import ChevronDown from "icons/rs_chevron_down.svg";
import * as styles from "./styles.module.scss";
const Select = ({ filterable = false, value, required, viewMode = false, customRequiredErrorMessage, onError, items, placeholder, disabled, listProps, itemRenderer, buttonRenderer: ButtonRenderer, onChange, onClosing, selectButtonProps, popoverProps, classes, selectRef, excludeValidation = false, testId, fixedItemsListWidth = false, autoFocus = false, ...selectProps }) => {
    const rootRef = useRef(null);
    const [error, setError] = useState();
    const [wrapperWidth, setWrapperWidth] = useState();
    const [popoverOpened, setPopoverOpened] = useState(false);
    const [popoverRef, setPopoverRef] = useState(null);
    const getErrorMessage = useCallback((value) => {
        if ((value === null || value === undefined) && required) {
            return customRequiredErrorMessage ?? "Select value";
        }
    }, [customRequiredErrorMessage, required]);
    const validateValue = useCallback((value) => {
        const errorMessage = getErrorMessage(value);
        setError(errorMessage);
        onError && onError();
        return !!errorMessage;
    }, [onError, getErrorMessage]);
    const handleChange = useCallback((item, event) => {
        !excludeValidation && validateValue(value);
        setPopoverOpened(false);
        onChange(item, event);
    }, [excludeValidation, onChange, validateValue, value]);
    useEffect(() => {
        popoverOpened && !excludeValidation && setError(undefined);
    }, [excludeValidation, popoverOpened]);
    useLayoutEffect(() => {
        if (popoverRef && wrapperWidth) {
            popoverRef.style.minWidth = `${wrapperWidth}px`;
            if (fixedItemsListWidth) {
                popoverRef.style.width = `${wrapperWidth}px`;
            }
        }
    }, [fixedItemsListWidth, popoverRef, wrapperWidth]);
    const handleResize = useCallback((entries) => {
        const { current } = rootRef;
        if (current) {
            const entry = entries.find(({ target }) => target === current);
            entry && setWrapperWidth(entry.contentRect.width);
        }
    }, []);
    const hideListener = useMemo(() => getHideListenerModifier((isReferenceHidden) => isReferenceHidden && setPopoverOpened(false)), []);
    const isDisabled = useMemo(() => disabled || items?.length === 0, [disabled, items?.length]);
    const defaultListRenderer = useCallback(({ items, itemsParentRef, renderItem }) => {
        const renderedItems = items.map(renderItem).filter((item) => item != null);
        return (React.createElement(ScrollBar, { autoHeight: true, autoHeightMax: listProps?.maxHeight ?? 247 },
            React.createElement(Menu, { ulRef: itemsParentRef }, renderedItems)));
    }, [listProps?.maxHeight]);
    const preparedSelectButtonProps = useMemo(() => ({
        value,
        placeholder,
        popoverOpened,
        onKeyDown: (e) => {
            if (e.key === "Tab" && popoverOpened && !viewMode) {
                setPopoverOpened(false);
            }
        },
        viewMode,
        disabled,
        customChevron: React.createElement(ChevronDown, null),
        ...selectButtonProps,
    }), [disabled, placeholder, popoverOpened, selectButtonProps, value, viewMode]);
    useEffect(() => {
        if (!autoFocus) {
            return;
        }
        if (rootRef.current) {
            const element = rootRef.current?.querySelector("div:first-of-type button");
            if (element) {
                setTimeout(() => element.focus(), 0);
            }
        }
    }, [autoFocus, selectRef]);
    return (React.createElement(ResizeSensor, { onResize: handleResize },
        React.createElement("div", { ref: rootRef, className: classNames(styles.selectWrapper, classes?.root, {
                [styles.unfilled]: !value,
                [styles.disabled]: !viewMode && isDisabled,
                [styles.viewMode]: viewMode,
                [styles.error]: !!error,
                [classes?.errorRoot ?? ""]: !!error,
                [classes?.selectOpen ?? ""]: !!classes?.selectOpen && popoverOpened,
            }), "data-testid": testId },
            React.createElement(BlueprintSelect, { ...selectProps, ref: selectRef, disabled: viewMode || isDisabled, items: items ?? [], filterable: filterable, itemRenderer: itemRenderer || DefaultItemRenderer, itemListRenderer: defaultListRenderer, onItemSelect: handleChange, popoverProps: {
                    isOpen: popoverOpened,
                    minimal: true,
                    fill: true,
                    matchTargetWidth: true,
                    popoverClassName: styles.popover,
                    portalClassName: classes?.portalClassName,
                    className: classNames(styles["popover-wrapper"], classes?.popoverWrapper),
                    popoverRef: setPopoverRef,
                    onInteraction: (isOpened) => setPopoverOpened(isOpened),
                    onClosing: () => {
                        !excludeValidation && validateValue(value);
                        onClosing && onClosing(value);
                    },
                    ...popoverProps,
                    modifiersCustom: [hideListener],
                    modifiers: {
                        preventOverflow: { options: { boundary: "clippingParents", tether: true } },
                        flip: { options: { rootBoundary: "viewport", flipVariations: true } },
                        ...popoverProps?.modifiers,
                    },
                    transitionDuration: popoverProps?.transitionDuration ?? 0,
                } },
                !ButtonRenderer ? (React.createElement(DefaultSelectButton, { className: classNames(classes?.selectButton, {
                        [styles.disabled]: disabled,
                        [styles.viewMode]: viewMode,
                    }), ...preparedSelectButtonProps })) : (React.createElement(ButtonRenderer, { ...preparedSelectButtonProps })),
                !viewMode && !!error && (React.createElement("p", { className: classNames(styles.errorMessage, classes?.errorMessage) }, error))))));
};
export default Select;
